/**
 * Browser-safe font-input allowlists, shared by the backend producer
 * (`font-entries.ts`) and the frontend mapper (`use-brand-fonts.ts`).
 *
 * Customer-controlled font family names, weights, and styles flow into
 * filesystem paths and raw CSS interpolation (`font-family: "<name>"`,
 * `font-weight: <weight>; font-style: <style>`). These allowlists are the
 * single source of truth for what's safe to pass through, on both sides of
 * the wire.
 *
 * This module intentionally has NO imports so it can be bundled into the
 * frontend without pulling in the server logger or Node built-ins.
 */

/** The only `font-style` values the allowlist admits. */
export type FontStyle = "normal" | "italic" | "oblique";

/**
 * The typographic identity of one font face: the CSS `font-family` it
 * belongs to plus the `font-weight` (a stop like "700" or a variable range
 * like "100 900") and optional `font-style` descriptors it registers under.
 * Every renderer-facing font shape carries this triple (as `Partial` when
 * the source may lack parsed metadata) — derive from it rather than
 * re-listing the fields.
 */
export type FontFaceIdentity = {
  family: string;
  weight: string;
  style?: FontStyle;
};

export const DEFAULT_WEIGHT = "400";
export const DEFAULT_STYLE: FontStyle = "normal";

// Disallow characters that would break out of CSS `font-family` quoting,
// path separators (path traversal), and ASCII control chars. Allows
// letters, digits, common punctuation found in real font family names.
export const SAFE_FAMILY_NAME = /^[A-Za-z0-9 _\-+.,&()]{1,64}$/;

// The allowlist admits punctuation, but a name that is *only* punctuation
// ("." or "-", the placeholders stripped name tables ship) is not a family
// anyone can request — reject it rather than registering an unusable face.
const HAS_ALPHANUMERIC = /[A-Za-z0-9]/;

// Weight + style flow into both filesystem paths (`<family>-<weight>-
// <style>.<ext>` in the bedrock font cache) and CSS interpolation
// (`font-weight: <weight>; font-style: <style>`). Without an allowlist
// a malicious `weight = "../../../tmp/x"` escapes the brand dir, and a
// malicious `style = "italic; } body {..."` injects CSS rules.
export const SAFE_FONT_WEIGHT =
  /^(100|200|300|400|500|600|700|800|900|normal|bold)$/;
export const SAFE_FONT_STYLE = /^(normal|italic|oblique)$/;

// Uploaded-file weights come from parsed binary metadata (OS/2
// `usWeightClass`, or a variable font's `wght` axis), so any CSS-valid
// integer 1–1000 or a "min max" range may appear — unlike user-entered
// custom-variant weights, which stay on the stricter `SAFE_FONT_WEIGHT`.
export const SAFE_FILE_FONT_WEIGHT =
  /^([1-9]\d{0,2}|1000)( ([1-9]\d{0,2}|1000))?$/;

/**
 * Trimmed family name when it passes the allowlist, `null` otherwise.
 * Rejects path separators, control chars, and anything that would break
 * CSS quoting (`"`, `'`, `;`, `{`, `}`, `<`, `>`, etc.). Callers decide
 * whether to skip the entry or log on `null`.
 */
export function sanitizeFamilyName(name: string | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  return SAFE_FAMILY_NAME.test(trimmed) && HAS_ALPHANUMERIC.test(trimmed)
    ? trimmed
    : null;
}

/**
 * Normalized font-weight. `value` falls back to `DEFAULT_WEIGHT` for absent
 * or unsafe input; `rejected` is `true` only when a value was present but
 * failed the allowlist, so callers can log that case specifically.
 */
export function sanitizeWeight(weight: string | undefined): {
  value: string;
  rejected: boolean;
} {
  if (!weight) return { value: DEFAULT_WEIGHT, rejected: false };
  if (SAFE_FONT_WEIGHT.test(weight)) return { value: weight, rejected: false };
  return { value: DEFAULT_WEIGHT, rejected: true };
}

/**
 * Weight parsed from an uploaded font's binary metadata, when it passes
 * the file-font allowlist; `null` otherwise. Unlike {@link sanitizeWeight}
 * there is no default fallback — absent or unsafe metadata means the file
 * falls back to legacy filename-derived registration, and a fabricated
 * "400" would mis-register a bold face.
 */
export function sanitizeFileFontWeight(
  weight: string | undefined,
): string | null {
  if (!weight) return null;
  const trimmed = weight.trim();
  return SAFE_FILE_FONT_WEIGHT.test(trimmed) ? trimmed : null;
}

/**
 * Normalized font-style. Same contract as {@link sanitizeWeight}. The
 * cast is sound: the allowlist regex admits exactly the {@link FontStyle}
 * members.
 */
export function sanitizeStyle(style: string | undefined): {
  value: FontStyle;
  rejected: boolean;
} {
  if (!style) return { value: DEFAULT_STYLE, rejected: false };
  if (SAFE_FONT_STYLE.test(style)) {
    return { value: style as FontStyle, rejected: false };
  }
  return { value: DEFAULT_STYLE, rejected: true };
}
